import { useState, useEffect } from 'react';
import { useNavigate, Routes, Route, NavLink } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  LayoutDashboard, 
  Wallet,
  ShoppingBag,
  TrendingUp,
  Coins,
  LogOut,
  Plus,
  ArrowUpRight,
  ArrowDownLeft,
  CreditCard,
  PiggyBank,
  Target,
  Lock,
  Loader2,
  ChevronRight,
  Sparkles,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';

// Dashboard Layout Component
function DashboardLayout({ children, auth }) {
  const navigate = useNavigate();
  const { user, logout } = auth;

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navItems = [
    { to: '/employee', icon: LayoutDashboard, label: 'Vue d\'ensemble', end: true },
    { to: '/employee/wallet', icon: Wallet, label: 'Mon Porte-monnaie' },
    { to: '/employee/shop', icon: ShoppingBag, label: 'Boutique' },
    { to: '/employee/investments', icon: TrendingUp, label: 'Investissements' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 to-purple-50/30 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-purple-100 flex flex-col fixed h-full">
        <div className="p-6 border-b border-purple-50">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-br from-violet-600 to-purple-600 rounded-xl flex items-center justify-center">
              <Wallet className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                InvesThune
              </span>
              <p className="text-xs text-violet-600 font-medium">Mon Espace</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `sidebar-item ${isActive ? 'active' : ''}`
              }
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-purple-50">
          <div className="flex items-center gap-3 mb-4 px-2">
            <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center">
              <span className="text-violet-700 font-semibold">
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-800 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-slate-600 hover:text-red-600 hover:bg-red-50"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Déconnexion
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64 p-8">
        {children}
      </main>
    </div>
  );
}

// Overview Page
function OverviewPage({ auth }) {
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const { token, user } = auth;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        
        const [walletRes, txnRes] = await Promise.all([
          fetch(`${API}/wallet`, { credentials: 'include', headers }),
          fetch(`${API}/wallet/transactions`, { credentials: 'include', headers })
        ]);
        
        if (walletRes.ok) setWallet(await walletRes.json());
        if (txnRes.ok) setTransactions(await txnRes.json());
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-violet-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="employee-dashboard">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Bonjour, {user?.name} 👋
        </h1>
        <p className="text-slate-600">Gérez votre porte-monnaie et vos investissements</p>
      </div>

      {/* Wallet Card - Hero */}
      <Card className="bg-gradient-to-br from-violet-600 via-purple-600 to-pink-500 text-white rounded-2xl mb-8 overflow-hidden relative">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <CardContent className="p-8 relative z-10">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-purple-200 text-sm mb-2">Solde disponible</p>
              <p className="text-5xl font-bold mb-4 counter" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {wallet?.balance?.toFixed(2) || '0.00'}€
              </p>
              <div className="flex gap-6 text-sm">
                <div>
                  <p className="text-purple-200">Total alimenté</p>
                  <p className="font-semibold">{wallet?.total_topped_up?.toFixed(2) || '0.00'}€</p>
                </div>
                <div>
                  <p className="text-purple-200">Total dépensé</p>
                  <p className="font-semibold">{wallet?.total_spent?.toFixed(2) || '0.00'}€</p>
                </div>
                <div>
                  <p className="text-purple-200">Total investi</p>
                  <p className="font-semibold">{wallet?.total_invested?.toFixed(2) || '0.00'}€</p>
                </div>
              </div>
            </div>
            <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center backdrop-blur">
              <Wallet className="w-8 h-8" />
            </div>
          </div>
          <div className="flex gap-3 mt-6">
            <NavLink to="/employee/wallet">
              <Button className="bg-white text-violet-700 hover:bg-purple-50 rounded-full">
                <Plus className="w-4 h-4 mr-2" />
                Alimenter
              </Button>
            </NavLink>
            <NavLink to="/employee/shop">
              <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-full">
                <ShoppingBag className="w-4 h-4 mr-2" />
                Acheter
              </Button>
            </NavLink>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Quick Actions */}
        <Card className="bg-white border-stone-200 rounded-2xl">
          <CardHeader>
            <CardTitle className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Actions rapides
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <NavLink to="/employee/wallet" className="block">
              <div className="flex items-center justify-between p-4 bg-stone-50 rounded-xl hover:bg-stone-100 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">Alimenter mon porte-monnaie</p>
                    <p className="text-sm text-slate-500">Ajoutez des fonds librement</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </div>
            </NavLink>
            <NavLink to="/employee/shop" className="block">
              <div className="flex items-center justify-between p-4 bg-stone-50 rounded-xl hover:bg-stone-100 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-pink-100 rounded-xl flex items-center justify-center">
                    <ShoppingBag className="w-5 h-5 text-pink-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">Profiter des promotions</p>
                    <p className="text-sm text-slate-500">Découvrez les offres partenaires</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </div>
            </NavLink>
            <NavLink to="/employee/investments" className="block">
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl hover:from-purple-100 hover:to-pink-100 transition-colors border border-purple-100">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-violet-600 to-purple-600 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">Investir mon épargne</p>
                    <p className="text-sm text-violet-600">Nouveau • Phase 2</p>
                  </div>
                </div>
                <span className="px-2 py-1 bg-violet-100 text-violet-700 text-xs font-medium rounded-full">
                  Bientôt
                </span>
              </div>
            </NavLink>
          </CardContent>
        </Card>

        {/* Recent Transactions */}
        <Card className="bg-white border-stone-200 rounded-2xl">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Transactions récentes
            </CardTitle>
            <NavLink to="/employee/wallet">
              <Button variant="ghost" size="sm" className="text-violet-600">
                Voir tout
              </Button>
            </NavLink>
          </CardHeader>
          <CardContent>
            {transactions.length === 0 ? (
              <div className="text-center py-8">
                <Wallet className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Aucune transaction</p>
                <p className="text-sm text-slate-400">Alimentez votre porte-monnaie pour commencer</p>
              </div>
            ) : (
              <div className="space-y-3">
                {transactions.slice(0, 5).map((txn) => (
                  <div key={txn.transaction_id} className="flex items-center justify-between py-3 border-b border-stone-100 last:border-0">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        txn.amount > 0 ? 'bg-violet-100' : 'bg-pink-100'
                      }`}>
                        {txn.amount > 0 ? (
                          <ArrowDownLeft className="w-5 h-5 text-violet-600" />
                        ) : (
                          <ArrowUpRight className="w-5 h-5 text-pink-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-slate-800 text-sm">{txn.description}</p>
                        <p className="text-xs text-slate-500">
                          {new Date(txn.created_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    </div>
                    <p className={`font-semibold ${txn.amount > 0 ? 'text-violet-600' : 'text-pink-600'}`}>
                      {txn.amount > 0 ? '+' : ''}{txn.amount.toFixed(2)}€
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Wallet Page
function WalletPage({ auth }) {
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { token } = auth;

  const fetchData = async () => {
    try {
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      const [walletRes, txnRes] = await Promise.all([
        fetch(`${API}/wallet`, { credentials: 'include', headers }),
        fetch(`${API}/wallet/transactions`, { credentials: 'include', headers })
      ]);
      
      if (walletRes.ok) setWallet(await walletRes.json());
      if (txnRes.ok) setTransactions(await txnRes.json());
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const handleTopUp = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${API}/wallet/topup`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ amount: parseFloat(amount) }),
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Erreur lors de l\'alimentation');

      toast.success(`${amount}€ ajoutés à votre porte-monnaie`);
      setIsOpen(false);
      setAmount('');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const quickAmounts = [10, 20, 50, 100];

  return (
    <div data-testid="wallet-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Mon Porte-monnaie
        </h1>
        <p className="text-slate-600">Gérez vos fonds et consultez votre historique</p>
      </div>

      {/* Balance Card */}
      <Card className="bg-gradient-to-br from-violet-600 via-purple-600 to-pink-500 text-white rounded-2xl mb-8">
        <CardContent className="p-8">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-200 text-sm mb-2">Solde actuel</p>
              <p className="text-5xl font-bold counter" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {wallet?.balance?.toFixed(2) || '0.00'}€
              </p>
            </div>
            <Dialog open={isOpen} onOpenChange={setIsOpen}>
              <DialogTrigger asChild>
                <Button className="bg-white text-violet-700 hover:bg-purple-50 rounded-full px-6" data-testid="topup-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Alimenter
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Alimenter mon porte-monnaie</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleTopUp} className="space-y-6">
                  <div className="space-y-2">
                    <Label>Montant (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="1"
                      placeholder="0.00"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      className="text-2xl font-bold text-center py-6"
                      required
                      data-testid="topup-amount-input"
                    />
                  </div>
                  <div className="flex gap-2">
                    {quickAmounts.map((q) => (
                      <Button
                        key={q}
                        type="button"
                        variant="outline"
                        className="flex-1 rounded-full"
                        onClick={() => setAmount(q.toString())}
                      >
                        {q}€
                      </Button>
                    ))}
                  </div>
                  <p className="text-sm text-slate-500 text-center">
                    💳 Paiement simulé - Mode démo
                  </p>
                  <Button
                    type="submit"
                    className="w-full bg-violet-600 hover:bg-violet-700 rounded-full py-6"
                    disabled={submitting || !amount}
                    data-testid="confirm-topup-btn"
                  >
                    {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : `Ajouter ${amount || '0'}€`}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="stat-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center">
                <ArrowDownLeft className="w-6 h-6 text-violet-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total alimenté</p>
                <p className="text-2xl font-bold text-violet-600 counter">{wallet?.total_topped_up?.toFixed(2) || '0.00'}€</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center">
                <ShoppingBag className="w-6 h-6 text-pink-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total dépensé</p>
                <p className="text-2xl font-bold text-pink-600 counter">{wallet?.total_spent?.toFixed(2) || '0.00'}€</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total investi</p>
                <p className="text-2xl font-bold text-purple-600 counter">{wallet?.total_invested?.toFixed(2) || '0.00'}€</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Transactions */}
      <Card className="bg-white border-stone-200 rounded-2xl">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Historique des transactions
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="w-8 h-8 border-4 border-emerald-800 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-12">
              <Wallet className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Aucune transaction</p>
            </div>
          ) : (
            <div className="space-y-3">
              {transactions.map((txn) => (
                <div key={txn.transaction_id} className="flex items-center justify-between p-4 bg-stone-50 rounded-xl">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      txn.type === 'topup' ? 'bg-violet-100' :
                      txn.type === 'purchase' ? 'bg-pink-100' :
                      txn.type === 'investment' ? 'bg-purple-100' : 'bg-stone-100'
                    }`}>
                      {txn.type === 'topup' && <ArrowDownLeft className="w-5 h-5 text-violet-600" />}
                      {txn.type === 'purchase' && <ShoppingBag className="w-5 h-5 text-pink-600" />}
                      {txn.type === 'investment' && <TrendingUp className="w-5 h-5 text-purple-600" />}
                      {txn.type === 'donation' && <PiggyBank className="w-5 h-5 text-pink-600" />}
                    </div>
                    <div>
                      <p className="font-medium text-slate-800">{txn.description}</p>
                      <p className="text-sm text-slate-500">
                        {new Date(txn.created_at).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                  <p className={`font-bold text-lg ${txn.amount > 0 ? 'text-violet-600' : 'text-pink-600'}`}>
                    {txn.amount > 0 ? '+' : ''}{txn.amount.toFixed(2)}€
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Shop Page (Promotions)
function ShopPage({ auth }) {
  const [promotions, setPromotions] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPromo, setSelectedPromo] = useState(null);
  const [purchaseAmount, setPurchaseAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { token } = auth;

  const fetchData = async () => {
    try {
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      const [promoRes, walletRes] = await Promise.all([
        fetch(`${API}/promotions/all`),
        fetch(`${API}/wallet`, { credentials: 'include', headers })
      ]);
      
      if (promoRes.ok) setPromotions(await promoRes.json());
      if (walletRes.ok) setWallet(await walletRes.json());
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const handlePurchase = async (e) => {
    e.preventDefault();
    if (!selectedPromo) return;
    setSubmitting(true);

    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${API}/purchases`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          promotion_id: selectedPromo.promotion_id,
          amount: parseFloat(purchaseAmount)
        }),
        credentials: 'include'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors de l\'achat');
      }

      const result = await response.json();
      toast.success(`Achat effectué ! Économie: ${(parseFloat(purchaseAmount) * selectedPromo.discount_percentage / 100).toFixed(2)}€`);
      setSelectedPromo(null);
      setPurchaseAmount('');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const calculateDiscount = () => {
    if (!purchaseAmount || !selectedPromo) return { discount: 0, final: 0 };
    const amount = parseFloat(purchaseAmount);
    const discount = amount * (selectedPromo.discount_percentage / 100);
    return { discount, final: amount - discount };
  };

  return (
    <div data-testid="shop-page">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Boutique Partenaires
          </h1>
          <p className="text-slate-600">Profitez des promotions exclusives avec votre porte-monnaie</p>
        </div>
        <div className="bg-violet-100 px-4 py-2 rounded-full">
          <span className="text-violet-700 font-semibold">Solde: {wallet?.balance?.toFixed(2) || '0.00'}€</span>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-emerald-800 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : promotions.length === 0 ? (
        <Card className="bg-white border-stone-200 rounded-2xl">
          <CardContent className="text-center py-16">
            <ShoppingBag className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-800 mb-2">Aucune promotion disponible</h3>
            <p className="text-slate-500">Les offres partenaires arriveront bientôt !</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {promotions.map((promo) => (
            <Card key={promo.promotion_id} className="bg-white border-stone-200 rounded-2xl overflow-hidden card-hover">
              {promo.banner_url && (
                <img 
                  src={promo.banner_url} 
                  alt={promo.title}
                  className="w-full h-40 object-cover"
                />
              )}
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {promo.title}
                  </h3>
                  <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-lg font-bold">
                    -{promo.discount_percentage}%
                  </span>
                </div>
                <p className="text-slate-600 text-sm mb-6">{promo.description}</p>
                
                <Dialog open={selectedPromo?.promotion_id === promo.promotion_id} onOpenChange={(open) => !open && setSelectedPromo(null)}>
                  <DialogTrigger asChild>
                    <Button 
                      className="w-full bg-violet-600 hover:bg-violet-700 rounded-full"
                      onClick={() => setSelectedPromo(promo)}
                      data-testid={`buy-${promo.promotion_id}`}
                    >
                      <ShoppingBag className="w-4 h-4 mr-2" />
                      Acheter
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>{promo.title}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handlePurchase} className="space-y-6">
                      <div className="p-4 bg-orange-50 rounded-xl text-center">
                        <p className="text-orange-800 font-bold text-2xl">-{promo.discount_percentage}%</p>
                        <p className="text-orange-600 text-sm">de réduction</p>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Montant de l'achat (€)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="1"
                          placeholder="0.00"
                          value={purchaseAmount}
                          onChange={(e) => setPurchaseAmount(e.target.value)}
                          className="text-xl font-bold text-center py-6"
                          required
                          data-testid="purchase-amount-input"
                        />
                      </div>

                      {purchaseAmount && (
                        <div className="p-4 bg-stone-50 rounded-xl space-y-2">
                          <div className="flex justify-between">
                            <span className="text-slate-600">Montant initial</span>
                            <span className="font-medium">{parseFloat(purchaseAmount).toFixed(2)}€</span>
                          </div>
                          <div className="flex justify-between text-orange-600">
                            <span>Réduction</span>
                            <span className="font-medium">-{calculateDiscount().discount.toFixed(2)}€</span>
                          </div>
                          <div className="flex justify-between text-lg font-bold border-t pt-2">
                            <span>Total à payer</span>
                            <span className="font-semibold text-violet-600">{calculateDiscount().final.toFixed(2)}€</span>
                          </div>
                        </div>
                      )}

                      <div className="flex items-center gap-2 p-3 bg-violet-50 rounded-xl">
                        <Wallet className="w-5 h-5 text-violet-600" />
                        <span className="text-sm text-violet-800">
                          Solde disponible: <strong>{wallet?.balance?.toFixed(2) || '0.00'}€</strong>
                        </span>
                      </div>

                      {purchaseAmount && calculateDiscount().final > (wallet?.balance || 0) && (
                        <div className="flex items-center gap-2 p-3 bg-red-50 rounded-xl">
                          <AlertCircle className="w-5 h-5 text-red-600" />
                          <span className="text-sm text-red-800">Solde insuffisant</span>
                        </div>
                      )}

                      <Button
                        type="submit"
                        className="w-full bg-violet-600 hover:bg-violet-700 rounded-full py-6"
                        disabled={submitting || !purchaseAmount || calculateDiscount().final > (wallet?.balance || 0)}
                        data-testid="confirm-purchase-btn"
                      >
                        {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Confirmer l\'achat'}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// Investments Page (Phase 2 Preview)
function InvestmentsPage({ auth }) {
  const [portfolios, setPortfolios] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const { token } = auth;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        
        const [portRes, invRes, walletRes] = await Promise.all([
          fetch(`${API}/portfolios`),
          fetch(`${API}/investments`, { credentials: 'include', headers }),
          fetch(`${API}/wallet`, { credentials: 'include', headers })
        ]);
        
        if (portRes.ok) setPortfolios(await portRes.json());
        if (invRes.ok) setInvestments(await invRes.json());
        if (walletRes.ok) setWallet(await walletRes.json());
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const getRiskColor = (level) => {
    const colors = {
      low: 'bg-emerald-100 text-emerald-700',
      medium: 'bg-orange-100 text-orange-700',
      high: 'bg-red-100 text-red-700'
    };
    return colors[level] || colors.medium;
  };

  const getRiskLabel = (level) => {
    const labels = { low: 'Prudent', medium: 'Équilibré', high: 'Dynamique' };
    return labels[level] || level;
  };

  return (
    <div data-testid="investments-page">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Investissements
          </h1>
          <span className="px-3 py-1 bg-emerald-100 text-emerald-700 text-sm font-medium rounded-full flex items-center gap-1">
            <Sparkles className="w-4 h-4" />
            Phase 2
          </span>
        </div>
        <p className="text-slate-600">Faites fructifier votre épargne avec nos partenaires gestionnaires</p>
      </div>

      {/* Coming Soon Banner */}
      <Card className="bg-gradient-to-br from-violet-600 via-purple-600 to-pink-500 text-white rounded-2xl mb-8 overflow-hidden relative">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <CardContent className="p-8 relative z-10">
          <div className="flex items-start gap-6">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0 backdrop-blur">
              <TrendingUp className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Bientôt disponible
              </h2>
              <p className="text-purple-100 mb-4">
                Nous travaillons avec des partenaires en gestion d'investissement pour vous permettre 
                d'investir votre épargne dans des portefeuilles d'actions diversifiés.
              </p>
              <div className="flex gap-4">
                <div className="flex items-center gap-2 text-sm">
                  <Lock className="w-4 h-4 text-pink-200" />
                  <span className="text-purple-100">Sécurisé</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Target className="w-4 h-4 text-pink-200" />
                  <span className="text-purple-100">Diversifié</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <PiggyBank className="w-4 h-4 text-pink-200" />
                  <span className="text-purple-100">Accessible</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Current Investments */}
      {investments.length > 0 && (
        <Card className="bg-white border-stone-200 rounded-2xl mb-8">
          <CardHeader>
            <CardTitle className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Mes investissements
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {investments.map((inv) => (
                <div key={inv.investment_id} className="flex items-center justify-between p-4 bg-stone-50 rounded-xl">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-800">{inv.portfolio_id}</p>
                      <p className="text-sm text-slate-500">Investi le {new Date(inv.created_at).toLocaleDateString('fr-FR')}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-violet-600">{inv.current_value?.toFixed(2)}€</p>
                    <p className="text-sm text-slate-500">Investi: {inv.amount?.toFixed(2)}€</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Preview Portfolios */}
      <h2 className="text-xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
        Portefeuilles disponibles prochainement
      </h2>
      
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-emerald-800 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {portfolios.map((portfolio) => (
            <Card key={portfolio.portfolio_id} className="bg-white border-stone-200 rounded-2xl overflow-hidden opacity-75 hover:opacity-100 transition-opacity">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-slate-600" />
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRiskColor(portfolio.risk_level)}`}>
                    {getRiskLabel(portfolio.risk_level)}
                  </span>
                </div>
                
                <h3 className="text-lg font-bold text-slate-800 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {portfolio.name}
                </h3>
                <p className="text-slate-600 text-sm mb-4">{portfolio.description}</p>
                
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between">
                    <span className="text-slate-500 text-sm">Rendement estimé</span>
                    <span className="font-semibold text-violet-600">{portfolio.expected_return}%/an</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500 text-sm">Investissement min.</span>
                    <span className="font-medium">{portfolio.min_investment}€</span>
                  </div>
                </div>

                <Button 
                  className="w-full rounded-full" 
                  variant="outline"
                  disabled
                >
                  <Lock className="w-4 h-4 mr-2" />
                  Bientôt disponible
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Info */}
      <Card className="bg-violet-50 border-violet-100 rounded-2xl mt-8">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <AlertCircle className="w-6 h-6 text-violet-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-violet-800 mb-1">Information importante</h4>
              <p className="text-violet-700 text-sm">
                Les investissements comportent des risques. Les performances passées ne préjugent pas des performances futures. 
                Nous vous recommandons de consulter un conseiller financier avant tout investissement.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Main Employee Dashboard Component
export default function EmployeeDashboard({ auth }) {
  return (
    <DashboardLayout auth={auth}>
      <Routes>
        <Route index element={<OverviewPage auth={auth} />} />
        <Route path="wallet" element={<WalletPage auth={auth} />} />
        <Route path="shop" element={<ShopPage auth={auth} />} />
        <Route path="investments" element={<InvestmentsPage auth={auth} />} />
      </Routes>
    </DashboardLayout>
  );
}
